import { Button } from '@/ui/components/Button';
import EntitiesUploadModal from '@/ui/components/entityItem/EntitiesUploadModal.vue';
import { isEntitiesUploadEnabled } from '@/ui/helpers/featureHelper';
import { defineComponent, h, ref } from 'vue';
import { level, pageTitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import * as qs from '@/ui/queryState';
import { secondaryNavbar, TabItem } from '@/ui/components/navigation/SecondaryNavbar';
import { EntityItemsTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { EntityItemIndexPage } from '@/ui/pages/entityItem/EntityItemIndexPage';
import { EntityFolderIndexPage } from '@/ui/pages/entityFolder/EntityFolderIndexPage';
import { RedirectButton } from '@/ui/components/button/RedirectButton';
import { EntityTypeIndexPage } from '@/ui/pages/entityType/EntityTypeIndexPage';
import { hasPermission, Permissions } from '@/ui/auth/auth';
import { useTask } from '@/ui/concerns';
import { list as getEntityTypeList } from '@/services/api/resources/entityType';
import { useCurrentRoute } from '@/ui/routes';
import { Tooltip } from '@/ui/components/Tooltip';

export const EntityItemsPage = defineComponent({
  name: 'EntityItemsPage',
  setup() {
    const getEntityTypes = useTask(
      () => getEntityTypeList({
        page: 1,
        perPage: 1,
        search: '',
        sort: '',
        dir: '',
      }),
      result => {
        if (!result.data.length) {
          activeTab.value = EntityItemsTabNames.TYPES;
        }
      },
    );

    const checkEntityTypes = (): void => {
      const currentRoute = useCurrentRoute();
      if (!currentRoute.value.query[queryTabName]) {
        getEntityTypes.run();
      }
    };

    const startTab = (): EntityItemsTabNames => {
      switch (true) {
        case hasPermission(Permissions.MANAGE_ENTITY_ITEMS):
          return EntityItemsTabNames.ITEMS;
        case hasPermission(Permissions.MANAGE_ENTITY_FOLDERS):
          return EntityItemsTabNames.FOLDERS;
        case hasPermission(Permissions.MANAGE_ENTITY_TYPES):
          return EntityItemsTabNames.TYPES;
        default:
          return EntityItemsTabNames.ITEMS;
      }
    };

    const activeTab = qs.stringEnum<EntityItemsTabNames>(
      queryTabName,
      Object.values(EntityItemsTabNames),
      startTab(),
      false,
    );

    const secondaryTabItems : TabItem[] = [
      {
        value: EntityItemsTabNames.ITEMS,
        label: t('common.entity-items'),
        icon: 'layer-group',
        underLineColor: 'blue',
        tooltipLabel: t('feature-explanation.entity-items'),
        hide: !hasPermission(Permissions.MANAGE_ENTITY_ITEMS),
      },
      {
        value: EntityItemsTabNames.FOLDERS,
        label: t('common.entity-folders'),
        icon: 'folder',
        underLineColor: 'blue',
        tooltipLabel: t('feature-explanation.entity-folders'),
        hide: !hasPermission(Permissions.MANAGE_ENTITY_FOLDERS),
      },
      {
        value: EntityItemsTabNames.TYPES,
        label: t('common.entity-types'),
        icon: 'bars',
        underLineColor: 'blue',
        tooltipLabel: t('feature-explanation.entity-types'),
        hide: !hasPermission(Permissions.MANAGE_ENTITY_TYPES),
      },
    ];

    const getTab = () => {
      switch (activeTab.value) {
        case EntityItemsTabNames.ITEMS:
          return EntityItemIndexPage;
        case EntityItemsTabNames.FOLDERS:
          return EntityFolderIndexPage;
        case EntityItemsTabNames.TYPES:
          return EntityTypeIndexPage;
        default:
          throw new Error('Invalid tab selected. Please use AnalyticsTabEnum enumeration');
      }
    };

    const isImportModalOpen = ref(false);
    const openImportModal = () => {
      isImportModalOpen.value = true;
    };
    const closeImportModal = () => {
      isImportModalOpen.value = false;
    };

    const renderButton = () => {
      switch (activeTab.value) {
        case EntityItemsTabNames.ITEMS:
          return hasPermission(Permissions.MANAGE_ENTITY_ITEMS)
            ? [
              isEntitiesUploadEnabled() && h(Tooltip, {
                props: {
                  label: t('entity-item.upload-modal.bulk-manage-tip'),
                  position: 'is-bottom',
                  multilined: true,
                },
              }, [
                h(Button, {
                  nativeOn: {
                    click: () => openImportModal(),
                  },
                  attrs: {
                    iconLeft: 'plus',
                    outlined: true,
                  },
                }, t('entity-item.upload-modal.bulk-manage')),
              ]),
              h(RedirectButton, {
                props: {
                  routeName: 'entityItemCreate',
                  text: t('entity-item.add'),
                  icon: 'plus',
                },
              }),
            ]
            : [];
        case EntityItemsTabNames.FOLDERS:
          return h(RedirectButton, {
            props: {
              routeName: 'entityFolderCreate',
              text: t('entity-folder.add'),
              icon: 'plus',
            },
          });
        case EntityItemsTabNames.TYPES:
          return h(RedirectButton, {
            props: {
              routeName: 'entityTypeCreate',
              text: t('entity-type.add'),
              icon: 'plus',
            },
          });
        default:
          return null;
      }
    };

    const renderModal = () => {
      return isEntitiesUploadEnabled() && h(EntitiesUploadModal, {
        props: {
          isOpen: isImportModalOpen.value,
          onClose: closeImportModal,
        },
      });
    };

    return function render() {
      checkEntityTypes();

      return h('div', [
        level(
          [
            pageTitle(t('common.entity-items')),
          ],
          [
            renderButton(),
          ],
        ),
        secondaryNavbar({
          props: { value: activeTab.value, tabItems: secondaryTabItems },
          on: {
            input: (value: EntityItemsTabNames) => {
              activeTab.value = value;
            },
          },
        }),
        h(getTab()),
        renderModal(),
      ]);
    };
  },
});
